import {useForm} from 'antd/es/form/Form';
import React, {useEffect, useState} from 'react';

import {TabbedForm} from 'components/@common/widgets/tabbed-form';
import {DirectoriesCompanyFormHeader} from 'components/directories-company-form/directories-company-form-header/directories-company-form-header';
import {
    TDirectoriesCompanyDto,
    EDirectoriesCompanyTabs,
} from 'components/directories-company-form/directories-company-form.types';
import {AisSignatories} from 'components/directories-company-form/tabs/ais-signatories/ais-signatories';
import {CommonInfo} from 'components/directories-company-form/tabs/common-info';
import {ModalOpenerComponent} from 'components/modal-opener-component';
import {resetLoadedData} from 'modules/data';
import {IPageTemplateProps} from 'pages';
import {EEntityType} from 'shared/constants/entities';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {directoriesCompanyActions} from 'store/slices/directories-company-slice/directories-company-slice';
import {loadAisSignatoriesByOrgId} from 'store/slices/directories-company-slice/directories-company-slice-thunks';
import {TAisSignatoryDto} from 'store/slices/directories-company-slice/directories-company-slice-types';
import {selectIsThunkPending} from 'store/slices/loading-state-slice';

import './directories-company-form.less';

type TDirectoriesCompanyFormProps = Pick<IPageTemplateProps, 'entityName' | 'entity'>;

export const DirectoriesCompanyForm = ({entityName, entity}: TDirectoriesCompanyFormProps) => {
    const dispatch = useAppDispatch();

    const isEntityExists = entity !== '-1';

    const [isCommonInfoLoading, setCommonInfoLoading] = useState<boolean>(false);
    const isAisSignatoriesLoading = useAppSelector(s => selectIsThunkPending(s, loadAisSignatoriesByOrgId.typePrefix));

    const isLoading = isEntityExists && (isCommonInfoLoading || isAisSignatoriesLoading);

    const [isDirty, setDirty] = useState<boolean>(false);
    const [isEditing, setEditing] = useState<boolean>(!isEntityExists);
    const [currentTab, setCurrentTab] = useState<string>(EDirectoriesCompanyTabs.common);

    const [tabNameToSwitch, setTabNameToSwitch] = useState<string | undefined>();
    const isTabChangeModalOpen = !!tabNameToSwitch;

    const [commonInfoForm] = useForm<TDirectoriesCompanyDto>();
    const [aisSignatoriesForm] = useForm<TAisSignatoryDto[]>();

    const currentForm = currentTab === 'common' ? commonInfoForm : aisSignatoriesForm;

    // reset data from all tabs
    useEffect(() => () => {
        if (isEntityExists) {
            dispatch(resetLoadedData(entityName, EEntityType.FORM));
            dispatch(directoriesCompanyActions.resetAisSignatories());
        }
    }, []);

    const hideModal = () => setTabNameToSwitch(undefined);

    const handleTabChange = (tabName: string | undefined) => {
        if (!tabName) return;

        if (isDirty) {
            setTabNameToSwitch(tabName);
        } else {
            setCurrentTab(tabName);
        }
    };

    const handleTabChangeOk = () => {
        if (!tabNameToSwitch) return;

        hideModal();
        setDirty(false);
        setCurrentTab(tabNameToSwitch);
    };

    const handleTabChangeCancel = () => {
        hideModal();
    };

    const resetFormStatus = () => {
        setDirty(false);
        setEditing(false);
    };

    return (
        <div className="directories-company-form">
            <ModalOpenerComponent
                isOpen={isTabChangeModalOpen}
                handleSave={handleTabChangeOk}
                handleCancel={handleTabChangeCancel}
                modalProps={{
                    title: 'Предупреждение',
                    centered: true,
                    width: '680px',
                }}
                controlLabels={{
                    save: 'Да',
                    cancel: 'Нет',
                }}
            >
                <div className="text-center">
                    При переходе на другую вкладку все несохраненные данные будут потеряны.
                    <br />Вы уверены что хотите уйти?
                </div>
            </ModalOpenerComponent>
            <TabbedForm
                classNames={{
                    card: 'directories-company-form-card',
                    tabsWrapper: 'directories-company-form-tabs-wrapper',
                }}
                title={(
                    <DirectoriesCompanyFormHeader
                        form={currentForm}
                        isDirty={isDirty}
                        setDirty={setDirty}
                        isEditing={isEditing}
                        setEditing={setEditing}
                    />
                )}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                defaultTabKey={EDirectoriesCompanyTabs.common}
                isLoading={isLoading}
                tip="Загрузка данных..."
                onTabChange={handleTabChange}
                tabs={[
                    {
                        key: EDirectoriesCompanyTabs.common,
                        title: 'Общие',
                        content: (
                            <CommonInfo
                                entityId={entity}
                                entityName={entityName}
                                form={commonInfoForm}
                                isEntityExists={isEntityExists}
                                setLoading={setCommonInfoLoading}
                                resetFormStatus={resetFormStatus}
                                isDisabled={!isEditing}
                                setDirty={setDirty}
                            />
                        ),
                    },
                    ({
                        key: EDirectoriesCompanyTabs.aisSignatories,
                        title: 'Подписанты данных АИС "Налог-3"',
                        isDisabled: !isEntityExists,
                        content: (
                            <AisSignatories
                                form={aisSignatoriesForm}
                                entityId={entity ? +entity : null}
                                isEntityExists={isEntityExists}
                                resetFormStatus={resetFormStatus}
                                isDisabled={!isEditing}
                                setDirty={setDirty}
                            />
                        ),
                    }),
                ]}
            />
        </div>
    );
};
